import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { formatActivityDate } from '@/dsm-components';
import usePermissions from '@/hooks/usePermissions';
import {
  useGetBrandsQuery,
  useGetManufacturerQuery,
  useSetManufacturerStatusMutation,
} from '@/services/equipmentManufacturers';
import { isErrorWithData } from '@/services/helpers';
import { PalmettoButton, PalmettoIcon, PalmettoModal, PalmettoTag } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EquipmentRecordStatus, ProgramTypeLabels, UserPermissions } from 'types';

import { EntityHistoryFeed } from '../shared/EntityHistoryFeed';

import { ActivateManufacturerModal } from './ActivateManufacturerModal';
import { EquipmentBrandsSection } from './EquipmentBrandsSection';
import { EquipmentBreadcrumb } from './EquipmentBreadcrumb';
import {
  brandPageLimit,
  emptyValueLabel,
  EquipmentRecordStatusLabels,
  EquipmentRecordStatusTagColors,
  formatEquipmentHistoryValue,
  manufacturerFieldLabel,
} from './equipmentLabels';

/** Above this many brands the modal reports the count alone; below it, every name is listed. */
const nameCascadedBrandsUpTo = 10;

const MetaField = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex flex-col gap-4">
    <div className="body-300 text-text-secondary">{label}</div>
    <div className="body-bold-300 text-text-primary">{value && value.trim() !== '' ? value : emptyValueLabel}</div>
  </div>
);

export const EquipmentManufacturerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: item, isLoading } = useGetManufacturerQuery(id!);
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);

  const [setManufacturerStatus, { isLoading: isChangingStatus }] = useSetManufacturerStatusMutation();
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isActivateOpen, setIsActivateOpen] = useState(false);

  const isActive = item?.status === EquipmentRecordStatus.active;

  // One query serves whichever modal applies: an active manufacturer's deactivate warning needs the
  // brands that will cascade off, an inactive one's activate picker needs the brands that can return.
  const { data: modalBrandsData } = useGetBrandsQuery(
    item
      ? {
          manufacturerId: item.id,
          status: isActive ? EquipmentRecordStatus.active : EquipmentRecordStatus.inactive,
          limit: brandPageLimit,
        }
      : skipToken,
  );
  const modalBrands = modalBrandsData?.data ?? [];

  const handleEdit = useCallback(() => navigate(`/admin/approved-vendors/manufacturers/${id}/edit`), [navigate, id]);

  const handleOpenDeactivate = useCallback(() => setIsDeactivateOpen(true), []);
  const handleOpenActivate = useCallback(() => setIsActivateOpen(true), []);
  const handleCloseDeactivate = useCallback(() => setIsDeactivateOpen(false), []);
  const handleCloseActivate = useCallback(() => setIsActivateOpen(false), []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!item) return;
    try {
      const result = await setManufacturerStatus({ id: item.id, status: EquipmentRecordStatus.inactive }).unwrap();
      toast.success(
        result.brandsChanged > 0
          ? `${item.name} deactivated, along with ${result.brandsChanged} ${result.brandsChanged === 1 ? 'brand' : 'brands'}.`
          : `${item.name} deactivated.`,
      );
      setIsDeactivateOpen(false);
    } catch (error) {
      console.error('Error deactivating equipment manufacturer:', error);
      toast.error(isErrorWithData(error) ? error.data.message : 'Failed to deactivate manufacturer.');
    }
  }, [item, setManufacturerStatus]);

  const handleConfirmActivate = useCallback(
    async (brandIds: string[]) => {
      if (!item) return;
      try {
        const result = await setManufacturerStatus({
          id: item.id,
          status: EquipmentRecordStatus.active,
          brandIds,
        }).unwrap();
        toast.success(
          result.brandsChanged > 0
            ? `${item.name} activated, along with ${result.brandsChanged} ${result.brandsChanged === 1 ? 'brand' : 'brands'}.`
            : `${item.name} activated.`,
        );
        setIsActivateOpen(false);
      } catch (error) {
        console.error('Error activating equipment manufacturer:', error);
        toast.error(isErrorWithData(error) ? error.data.message : 'Failed to activate manufacturer.');
      }
    },
    [item, setManufacturerStatus],
  );

  if (isLoading) {
    return (
      <DsmPageContainer title="Loading...">
        <div>Loading...</div>
      </DsmPageContainer>
    );
  }

  if (!item) {
    return (
      <DsmPageContainer title="Not Found">
        <div>Equipment manufacturer not found.</div>
      </DsmPageContainer>
    );
  }

  const aliases = item.aliases ?? [];

  return (
    <DsmPageContainer title={item.name}>
      <PageHeader title={item.name} eyebrow={<EquipmentBreadcrumb current={item.name} />}>
        <div className="flex items-center gap-8">
          <PalmettoTag
            label={EquipmentRecordStatusLabels[item.status] ?? item.status}
            color={EquipmentRecordStatusTagColors[item.status]}
          />
          {canEditAvl && (
            <>
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleEdit}
                leadingIcon={<PalmettoIcon name="penEdit" size="sm" />}
              >
                Edit
              </PalmettoButton>
              {isActive ? (
                <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleOpenDeactivate}>
                  Deactivate
                </PalmettoButton>
              ) : (
                <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleOpenActivate}>
                  Activate
                </PalmettoButton>
              )}
            </>
          )}
        </div>
      </PageHeader>

      <div className="mx-6 mb-16 grid grid-cols-3 gap-16">
        <div className="flex flex-col gap-4">
          <div className="body-300 text-text-secondary">Program Types</div>
          <div className="gap-1 flex flex-row flex-wrap">
            {item.programTypes.length === 0
              ? emptyValueLabel
              : item.programTypes.map((programType) => (
                  <PalmettoTag key={programType} label={ProgramTypeLabels[programType] ?? programType} />
                ))}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="body-300 text-text-secondary">Aliases</div>
          <div className="body-bold-300 text-text-primary">
            {aliases.length === 0
              ? emptyValueLabel
              : aliases.map((alias) => (alias.source ? `${alias.source}: ${alias.value}` : alias.value)).join(', ')}
          </div>
        </div>
        <MetaField label="Active Brands" value={String(item.activeBrandCount)} />
        <MetaField
          label="Created"
          value={item.meta?.createdAt ? formatActivityDate(item.meta.createdAt) : undefined}
        />
        <MetaField
          label="Last Modified"
          value={item.meta?.modifiedAt ? formatActivityDate(item.meta.modifiedAt) : undefined}
        />
        <MetaField label="Modified By" value={item.modifiedByName ?? item.createdByName} />
      </div>

      <div className="mx-6 mb-16">
        <EquipmentBrandsSection manufacturerId={item.id} canEdit={canEditAvl} />
      </div>

      <div className="mx-6 mb-16 flex flex-col gap-4">
        <h2 className="text-left heading-200 text-text-primary">Change History</h2>
        <EntityHistoryFeed
          history={item.history ?? []}
          fieldLabel={manufacturerFieldLabel}
          formatValue={formatEquipmentHistoryValue}
        />
      </div>

      {isDeactivateOpen && (
        <PalmettoModal
          open
          onClose={handleCloseDeactivate}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleCloseDeactivate}
                disabled={isChangingStatus}
              >
                Cancel
              </PalmettoButton>
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleConfirmDeactivate}
                disabled={isChangingStatus}
              >
                Deactivate
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-16">
            <h2>Deactivate {item.name}?</h2>
            {item.activeBrandCount > 0 ? (
              <p className="text-text-secondary">
                Its {item.activeBrandCount} active {item.activeBrandCount === 1 ? 'brand' : 'brands'} will be
                deactivated along with it.
              </p>
            ) : (
              <p className="text-text-secondary">It has no active brands.</p>
            )}
            {item.activeBrandCount > 0 && modalBrands.length > 0 && modalBrands.length <= nameCascadedBrandsUpTo && (
              <ul className="list-disc pl-16 text-text-secondary">
                {modalBrands.map((brand) => (
                  <li key={brand.id}>{brand.name}</li>
                ))}
              </ul>
            )}
          </div>
        </PalmettoModal>
      )}

      {isActivateOpen && (
        <ActivateManufacturerModal
          manufacturerName={item.name}
          inactiveBrands={modalBrands}
          isSubmitting={isChangingStatus}
          onCancel={handleCloseActivate}
          onConfirm={handleConfirmActivate}
        />
      )}
    </DsmPageContainer>
  );
};
